import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, AlertTriangle, Calendar, Clock, CheckCircle, Zap, CircleDot, Info, Download, FileText, AlertCircle } from 'lucide-react';
import PayoutsCard from '../../components/PayoutsCard';
import PendingRecapBadge from '../../components/PendingRecapBadge';
import SettlementChallengeModal from '../../components/talent/SettlementChallengeModal';
import MissingPaymentChallengeModal from '../../components/talent/MissingPaymentChallengeModal';
import api from '../../utils/api';
import { formatDateMonthDay, formatDateFull } from '../../utils/timeFormat';
import { statusConfig, SETTLEMENT_TIMELINE, timelineIndex } from '../../utils/settlementStatus';

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// Visual lifecycle: Shift Completed -> Pending Approval -> Approved -> Processing -> Paid
const SettlementTimeline = ({ status }) => {
  const active = timelineIndex(status);
  return (
    <div className="flex items-center w-full" data-testid="settlement-timeline">
      {SETTLEMENT_TIMELINE.map((step, i) => {
        const done = i <= active;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={`w-3.5 h-3.5 rounded-full ${done ? (status === 'under_review' ? 'bg-purple-500' : 'bg-cyan-500') : 'bg-gray-200'}`} />
              <span className={`mt-1 text-[10px] leading-tight text-center ${done ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>{step.label}</span>
            </div>
            {i < SETTLEMENT_TIMELINE.length - 1 && (
              <div className={`h-0.5 flex-1 mx-1 ${i < active ? (status === 'under_review' ? 'bg-purple-400' : 'bg-cyan-400') : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const TalentSettlements = () => {
  const navigate = useNavigate();
  const [settlements, setSettlements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [detail, setDetail] = useState(null);
  const [challengeFor, setChallengeFor] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [instantPayAvailable, setInstantPayAvailable] = useState(false);
  const [criticalPay, setCriticalPay] = useState(null);

  useEffect(() => {
    fetchSettlements();
    api.get('/talent/payout/critical-pay/me')
      .then((r) => setCriticalPay(r.data))
      .catch(() => setCriticalPay(null));
    (async () => {
      try {
        const info = await api.get('/monetization/instant-payout/info');
        const feature_on = info.data?.feature_enabled !== false && !info.data?.disabled;
        if (!feature_on) { setInstantPayAvailable(false); return; }
        const eligible = await api.get('/monetization/eligible-bookings');
        const bookings = Array.isArray(eligible.data?.bookings) ? eligible.data.bookings : [];
        setInstantPayAvailable(bookings.length > 0);
      } catch (_) { setInstantPayAvailable(false); }
    })();
  }, []);

  const fetchSettlements = async () => {
    try {
      const [listRes, summaryRes] = await Promise.all([
        api.get('/talent/settlements'),
        api.get('/talent/settlements/summary'),
      ]);
      setSettlements(Array.isArray(listRes.data) ? listRes.data : []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching settlements:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadStatement = async (s) => {
    try {
      const res = await api.get(`/talent/settlements/${s.booking_id}/statement.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `settlement-statement-${s.booking_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('statement download failed', e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#03DFFC]"></div>
      </div>
    );
  }

  const tiles = [
    { key: 'pending_approval', label: 'Pending Approval Settlement', amount: summary?.pending_approval_amount, color: 'text-amber-600' },
    { key: 'approved', label: 'Approved / Upcoming Settlement', amount: summary?.approved_upcoming_amount, color: 'text-blue-600' },
    { key: 'payment_processing', label: 'Settlement Processing', amount: summary?.processing_amount, color: 'text-indigo-600' },
    { key: 'paid', label: 'Paid Settlements', amount: summary?.paid_amount, color: 'text-green-600' },
  ];

  const visible = activeFilter
    ? settlements.filter((s) => s.status === activeFilter)
    : settlements;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-settlements-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} className="text-[#03DFFC] hover:text-[#03DFFC]/80 hover:bg-gray-100">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h1 className="text-2xl font-bold text-gray-900">My Settlements</h1>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={() => setShowMissing(true)} data-testid="report-missing-payment-btn">
              <AlertCircle className="w-4 h-4 mr-2" /> Report missing shift / payment
            </Button>
            <Button variant="outline" onClick={() => navigate('/talent/1099')} data-testid="view-1099-btn">
              <FileText className="w-4 h-4 mr-2" /> Tax Center
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-6">Every completed gig shows your hours, contract rate, settlement amount and where it is in the settlement process. Worked an event that isn’t listed here? Use “Report missing shift / payment”.</p>

        <MissingPaymentChallengeModal
          open={showMissing}
          onClose={() => setShowMissing(false)}
          onSubmitted={fetchSettlements}
        />

        <div className="mb-6" data-testid="settlement-pending-recap-slot">
          <PendingRecapBadge />
        </div>

        {criticalPay?.critical_pay_enabled && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="talent-critical-pay-banner">
            <Zap className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800 text-sm">You're approved for Critical Pay</p>
              <p className="text-xs text-amber-700 mt-0.5">{criticalPay.note}</p>
            </div>
          </div>
        )}

        {summary?.under_review_amount > 0 && (
          <Alert className="mb-6 bg-purple-50 border-purple-200" data-testid="under-review-banner">
            <Info className="h-4 w-4 text-purple-600" />
            <AlertDescription className="text-gray-700">
              <span className="font-medium text-purple-800">{fmt(summary.under_review_amount)} under review.</span>{' '}
              One or more settlements are being reviewed following a challenge. Reviews may take up to 5 additional business days.
            </AlertDescription>
          </Alert>
        )}

        {/* Summary tiles — clickable to filter the list below */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {tiles.map((t) => {
              const isActive = activeFilter === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveFilter(isActive ? null : t.key)}
                  data-testid={`settlement-tile-${t.key}`}
                  className={`text-left rounded-xl border bg-white shadow-sm px-4 py-4 transition hover:shadow-md ${isActive ? 'ring-2 ring-[#03DFFC] border-[#03DFFC]' : 'border-gray-200'}`}
                >
                  <p className="text-xs font-medium text-gray-500 mb-1">{t.label}</p>
                  <p className={`text-2xl font-bold ${t.color}`}>{fmt(t.amount)}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{summary.counts?.[t.key] || 0} gig{(summary.counts?.[t.key] || 0) === 1 ? '' : 's'}</p>
                </button>
              );
            })}
          </div>
        )}

        {activeFilter && (
          <div className="mb-4">
            <Button variant="outline" size="sm" onClick={() => setActiveFilter(null)} data-testid="clear-settlement-filter">
              Clear filter
            </Button>
          </div>
        )}

        <div className="mb-6"><PayoutsCard /></div>

        {instantPayAvailable && (
          <Card className="mb-6 bg-gradient-to-r from-[#03DFFC]/10 to-[#03DFFC]/5 border-[#03DFFC]/30" data-testid="instant-payout-cta-card">
            <CardContent className="py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#03DFFC]/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-[#03DFFC]" />
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Instant settlement available for one or more completed gigs</p>
                  <p className="text-gray-500 text-sm">Eligibility varies by gig, payment type, and account status. Fees apply.</p>
                </div>
              </div>
              <Button onClick={() => navigate('/talent/instant-payout')} className="bg-[#03DFFC] hover:bg-[#03DFFC]/90 text-white" data-testid="instant-payout-cta-btn">
                <Zap className="w-4 h-4 mr-2" /> Review options
              </Button>
            </CardContent>
          </Card>
        )}

        {summary && !summary.has_workers_comp && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-gray-600">
              <span className="font-medium text-gray-900">Platform Adjustment Notice:</span> {summary.oa_note}
            </AlertDescription>
          </Alert>
        )}

        {/* Settlement list */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-900">Settlement History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {visible.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                {activeFilter ? 'No settlements in this status.' : 'No settlements yet. Complete gigs to start earning settlements!'}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {visible.map((s) => {
                  const cfg = statusConfig(s.status);
                  return (
                    <div
                      key={s.booking_id}
                      className="p-4 cursor-pointer hover:bg-slate-50 transition"
                      data-testid={`settlement-item-${s.booking_id}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetail(s)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail(s); } }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{s.gig_name || s.role_name}</h3>
                          <p className="text-sm text-gray-500">{s.role_name} • {formatDateMonthDay(s.date_worked)}</p>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.badge}`} data-testid={`settlement-status-${s.booking_id}`}>
                          <CircleDot className="w-3 h-3" /> {s.is_overdue ? 'Still Processing' : cfg.label}
                        </span>
                      </div>

                      {s.critical_pay && (
                        <div className="mb-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`settlement-critical-pay-${s.booking_id}`}>
                          <Zap className="w-3 h-3" /> Critical Pay
                        </div>
                      )}

                      {s.is_overdue && !s.status_note && (
                        <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid={`settlement-delayed-${s.booking_id}`}>
                          <AlertTriangle className="w-3.5 h-3.5" /> Settlement Delayed — still processing (expected {formatDateFull(s.expected_settlement_date)})
                        </div>
                      )}
                      {s.status_note && (
                        <div className="mb-2 flex items-start gap-1.5 text-xs font-medium text-cyan-700" data-testid={`settlement-note-${s.booking_id}`}>
                          <Info className="w-3.5 h-3.5 mt-0.5" /> {s.status_note}
                        </div>
                      )}

                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {s.hours_worked ?? '—'} hrs @ {s.contract_rate != null ? `$${Number(s.contract_rate).toFixed(2)}/hr` : '—'}
                          </span>
                          <span className="text-gray-900 font-bold text-base" data-testid={`settlement-amount-${s.booking_id}`}>
                            {fmt(s.settlement_amount)}
                          </span>
                        </div>
                        {s.reimbursements > 0 && (
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>Includes reimbursements</span>
                            <span>{fmt(s.reimbursements)}</span>
                          </div>
                        )}
                        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-gray-200 text-xs">
                          <span className="text-gray-500">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {s.settlement_terms_label || 'Settlement terms per gig'}
                          </span>
                          {s.status === 'paid' && s.paid_date ? (
                            <span className="text-green-700 font-medium">Paid {formatDateFull(s.paid_date)}</span>
                          ) : s.expected_settlement_date ? (
                            <span className="text-gray-700 font-medium">Expected {formatDateFull(s.expected_settlement_date)}</span>
                          ) : null}
                        </div>
                        <p className="text-xs text-gray-500 pt-1">{s.status_note ? s.status_note : s.status === 'paid' && s.paid_date ? `Your settlement was issued on ${formatDateFull(s.paid_date)}.` : s.is_overdue ? 'We know the expected settlement date has passed. No action is needed — our accounting team is completing your settlement.' : cfg.explanation}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Settlement Details drawer */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="settlement-detail-drawer">
          <DialogHeader>
            <DialogTitle className="text-base">Settlement Details</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Full settlement information for this gig.</DialogDescription>
          </DialogHeader>
          {detail && (() => {
            const s = detail;
            const cfg = statusConfig(s.status);
            return (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-900">{s.gig_name || s.role_name}</h3>
                  <p className="text-sm text-slate-600">{s.role_name}{s.venue_name ? ` • ${s.venue_name}` : ''}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Date worked {formatDateFull(s.date_worked)}</p>
                </div>

                <div className={`rounded-lg p-3 ${s.is_overdue && !s.status_note ? 'bg-rose-50 text-rose-800 border border-rose-200' : cfg.badge}`}>
                  <p className="text-xs font-semibold">{s.status_note ? 'Settlement Update' : s.is_overdue && s.status !== 'paid' ? 'Settlement Delayed · Still Processing' : cfg.label}</p>
                  <p className="text-xs mt-1 opacity-90">{s.status_note ? s.status_note : s.status === 'paid' && s.paid_date ? `Your settlement was issued on ${formatDateFull(s.paid_date)}.` : s.is_overdue ? `We know the expected settlement date (${formatDateFull(s.expected_settlement_date)}) has passed. No action is needed — our accounting team is completing your settlement.` : cfg.explanation}</p>
                </div>

                <SettlementTimeline status={s.status} />

                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
                  <div className="flex justify-between p-2.5"><span className="text-slate-600">Hours worked</span><span className="font-medium text-slate-900">{s.hours_worked ?? '—'} hrs</span></div>
                  <div className="flex justify-between p-2.5"><span className="text-slate-600">Contract rate</span><span className="font-medium text-slate-900">{s.contract_rate != null ? `$${Number(s.contract_rate).toFixed(2)}/hr` : '—'}</span></div>
                  <div className="flex justify-between p-2.5"><span className="text-slate-600">Reimbursements / additions</span><span className="text-slate-900">{s.reimbursements ? fmt(s.reimbursements) : '—'}</span></div>
                  {s.oa_deduction > 0 && (
                    <div className="flex justify-between p-2.5"><span className="text-slate-600">OA insurance{s.oa_rate_per_hour ? ` (@ $${Number(s.oa_rate_per_hour).toFixed(2)}/hr)` : ''}</span><span className="text-rose-600">-{fmt(s.oa_deduction)}</span></div>
                  )}
                  <div className="flex justify-between p-2.5 bg-slate-50"><span className="text-slate-700 font-semibold">Settlement amount</span><span className="font-bold text-slate-900" data-testid="detail-settlement-amount">{fmt(s.settlement_amount)}</span></div>
                </div>

                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
                  <div className="flex justify-between p-2.5"><span className="text-slate-600">Settlement terms</span><span className="text-slate-900 text-right">{s.settlement_terms_label || '—'}</span></div>
                  <div className="flex justify-between p-2.5">
                    <span className="text-slate-600">Expected settlement date</span>
                    <span className="text-slate-900 font-medium">{s.status === 'paid' && s.paid_date ? formatDateFull(s.paid_date) : (s.expected_settlement_date ? formatDateFull(s.expected_settlement_date) : '—')}</span>
                  </div>
                  <div className="flex justify-between p-2.5"><span className="text-slate-600">Status</span><span className="font-medium text-slate-900">{cfg.label}</span></div>
                  {s.stripe_transfer_id && (
                    <div className="flex justify-between p-2.5"><span className="text-slate-600">Transfer ID</span><span className="text-slate-900 font-mono text-[10px] break-all">{s.stripe_transfer_id}</span></div>
                  )}
                </div>

                {s.settlement_terms_disclaimer && (
                  <p className="text-[11px] text-slate-500 leading-relaxed">{s.settlement_terms_disclaimer}</p>
                )}

                {s.status === 'under_review' ? (
                  <Alert className="bg-purple-50 border-purple-200">
                    <Info className="h-4 w-4 text-purple-600" />
                    <AlertDescription className="text-xs text-slate-700">
                      A challenge is under review for this settlement. Your settlement amount stays visible while we review (up to 5 additional business days).
                    </AlertDescription>
                  </Alert>
                ) : s.can_challenge ? (
                  <Button
                    variant="outline"
                    className="w-full border-slate-300 text-slate-700"
                    data-testid="open-challenge-btn"
                    onClick={() => { setChallengeFor(s); setDetail(null); }}
                  >
                    Challenge Settlement
                  </Button>
                ) : null}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => detail && downloadStatement(detail)} data-testid="settlement-statement-btn" className="border-slate-300 text-slate-700">
              <Download className="w-4 h-4 mr-2" /> Download statement
            </Button>
            <Button onClick={() => setDetail(null)} data-testid="settlement-detail-close" className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <CheckCircle className="w-4 h-4 mr-2" /> Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SettlementChallengeModal
        open={!!challengeFor}
        settlement={challengeFor}
        onClose={() => setChallengeFor(null)}
        onSubmitted={fetchSettlements}
      />
    </div>
  );
};

export default TalentSettlements;
